/**
 * Sticky Add to Cart Button
 * Shows a sticky add to cart button when user scrolls past the main product form
 */

class StickyAddToCart {
  constructor() {
    this.stickyButton = document.querySelector('.sticky-add-to-cart');
    this.mainForm = document.querySelector('.product__form');
    this.mainAddToCartButton = document.querySelector('.product__submit__add[data-add-to-cart]');
    this.stickyForm = this.stickyButton?.querySelector('.sticky-add-to-cart__form form');
    this.isVisible = false;
    this.throttleTimer = null;

    if (!this.stickyButton || !this.mainForm || !this.mainAddToCartButton) {
      return;
    }

    this.init();
  }

  init() {
    // Bind events
    this.bindScrollEvent();
    this.bindFormSync();
    this.bindStickyFormSubmit();
    this.bindQuantityButtons();

    // Initial check
    this.checkVisibility();
  }

  bindScrollEvent() {
    window.addEventListener('scroll', () => {
      if (this.throttleTimer) return;
      
      this.throttleTimer = setTimeout(() => {
        this.checkVisibility();
        this.throttleTimer = null;
      }, 16); // ~60fps
    });
  }

  checkVisibility() {
    if (!this.mainAddToCartButton) return;

    const buttonRect = this.mainAddToCartButton.getBoundingClientRect();
    
    // Show sticky button when main button is scrolled out of view (above viewport)
    const shouldShow = buttonRect.bottom < 0;

    if (shouldShow && !this.isVisible) {
      this.show();
    } else if (!shouldShow && this.isVisible) {
      this.hide();
    }
  }

  show() {
    this.isVisible = true;
    this.stickyButton.classList.add('is-visible');
    this.syncFormData();
  }

  hide() {
    this.isVisible = false;
    this.stickyButton.classList.remove('is-visible');
  }

  bindFormSync() {
    if (!this.mainForm) return;

    // Sync when variant changes
    this.mainForm.addEventListener('change', () => {
      if (this.isVisible) {
        this.syncFormData();
      }
    });

    // Listen for variant changes from theme's variant picker
    document.addEventListener('variant:change', () => {
      if (this.isVisible) {
        setTimeout(() => {
          this.syncFormData();
        }, 100); // Small delay to ensure DOM is updated
      }
    });

    // Listen for price updates
    document.addEventListener('price:change', () => {
      if (this.isVisible) {
        this.updatePrices();
      }
    });

    // Sync when quantity changes
    const mainQuantityInput = this.mainForm.querySelector('input[name="quantity"]');
    if (mainQuantityInput) {
      mainQuantityInput.addEventListener('input', () => {
        if (this.isVisible) {
          this.syncQuantity();
        }
      });
    }

    // Use MutationObserver to watch for price changes
    this.observePriceChanges();
  }

  observePriceChanges() {
    const priceElement = document.querySelector('[data-product-price]');
    if (!priceElement) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          if (this.isVisible) {
            this.updatePrices();
            this.updateVariantTitle();
          }
        }
      });
    });

    observer.observe(priceElement, {
      childList: true,
      subtree: true,
      characterData: true
    });

    // Store observer for cleanup
    this.priceObserver = observer;
  }

  syncFormData() {
    if (!this.stickyForm || !this.mainForm) return;

    // Sync variant ID
    const mainVariantInput = this.mainForm.querySelector('input[name="id"]');
    const stickyVariantInput = this.stickyForm.querySelector('input[name="id"]');
    
    if (mainVariantInput && stickyVariantInput) {
      stickyVariantInput.value = mainVariantInput.value;
    }

    // Sync quantity
    this.syncQuantity();

    // Sync any other form inputs (properties, etc.)
    const mainInputs = this.mainForm.querySelectorAll('input[name^="properties"], input[name^="attributes"]');
    mainInputs.forEach(input => {
      const stickyInput = this.stickyForm.querySelector(`input[name="${input.name}"]`);
      if (stickyInput) {
        stickyInput.value = input.value;
        stickyInput.checked = input.checked;
      }
    });

    // Update button state and text
    this.updateStickyButton();
  }

  syncQuantity() {
    const mainQuantityInput = this.mainForm?.querySelector('input[name="quantity"]');
    const stickyQuantityInput = this.stickyForm?.querySelector('input[name="quantity"]');
    
    if (mainQuantityInput && stickyQuantityInput) {
      stickyQuantityInput.value = mainQuantityInput.value;
    }
  }

  updateStickyButton() {
    const mainButton = this.mainAddToCartButton;
    const stickyButton = this.stickyForm?.querySelector('button[type="submit"]');
    
    if (!mainButton || !stickyButton) return;

    // Copy button state
    stickyButton.disabled = mainButton.disabled;
    
    // Copy button text content
    const mainButtonText = mainButton.querySelector('[data-add-to-cart-text]');
    const stickyButtonText = stickyButton.querySelector('[data-add-to-cart-text]');
    
    if (mainButtonText && stickyButtonText) {
      stickyButtonText.innerHTML = mainButtonText.innerHTML;
    }

    // Update prices
    this.updatePrices();
    
    // Update variant title
    this.updateVariantTitle();
  }

  updatePrices() {
    // Find main price elements
    const mainPrice = document.querySelector('[data-product-price]');
    const mainComparePrice = document.querySelector('[data-compare-price]');
    
    // Find sticky price elements
    const stickyPrice = this.stickyButton?.querySelector('[data-sticky-product-price]');
    const stickyComparePrice = this.stickyButton?.querySelector('[data-sticky-compare-price]');
    
    if (mainPrice && stickyPrice) {
      stickyPrice.textContent = mainPrice.textContent;
      
      // Copy sale class if exists
      if (mainPrice.classList.contains('product__price--sale')) {
        stickyPrice.classList.add('product__price--sale');
      } else {
        stickyPrice.classList.remove('product__price--sale');
      }
    }
    
    // Handle compare at price
    if (mainComparePrice && stickyComparePrice) {
      const mainComparePriceText = mainComparePrice.textContent.trim();
      
      if (mainComparePriceText) {
        stickyComparePrice.textContent = mainComparePriceText;
        stickyComparePrice.style.display = '';
      } else {
        stickyComparePrice.style.display = 'none';
      }
    } else if (stickyComparePrice) {
      stickyComparePrice.style.display = 'none';
    }
  }

  updateVariantTitle() {
    const stickyVariantTitle = this.stickyButton?.querySelector('[data-sticky-variant-title]');
    
    if (!stickyVariantTitle) return;
    
    // Get current variant ID
    const variantId = this.mainForm?.querySelector('input[name="id"]')?.value;
    
    if (!variantId) {
      stickyVariantTitle.style.display = 'none';
      return;
    }
    
    // Method 1: Use the form's data-variant-title attribute (updated by theme)
    const formVariantTitle = this.mainForm?.getAttribute('data-variant-title');
    if (formVariantTitle && formVariantTitle !== 'Default Title') {
      stickyVariantTitle.textContent = formVariantTitle;
      stickyVariantTitle.style.display = '';
      return;
    }
    
    // Method 2: Build from selected option values
    const selectedOptions = [];
    const selects = this.mainForm?.querySelectorAll('select[name*="option"]');
    
    selects?.forEach(select => {
      if (select.value && select.value.trim()) {
        selectedOptions.push(select.value.trim());
      }
    });
    
    if (selectedOptions.length > 0) {
      stickyVariantTitle.textContent = selectedOptions.join(' / ');
      stickyVariantTitle.style.display = '';
    } else {
      stickyVariantTitle.style.display = 'none';
    }
  }

  bindStickyFormSubmit() {
    if (!this.stickyForm) return;

    this.stickyForm.addEventListener('submit', (e) => {
      e.preventDefault();
      
      // Sync data one more time before submitting
      this.syncFormData();
      
      // Trigger the main form submission
      if (this.mainForm) {
        // Create and dispatch a submit event on the main form
        const submitEvent = new Event('submit', {
          bubbles: true,
          cancelable: true
        });
        
        this.mainForm.dispatchEvent(submitEvent);
      }
    });
  }

  // Update product info in sticky bar
  updateProductInfo(title, price, image) {
    const titleElement = this.stickyButton?.querySelector('.sticky-add-to-cart__title');
    const priceElement = this.stickyButton?.querySelector('.sticky-add-to-cart__price');
    const imageElement = this.stickyButton?.querySelector('.sticky-add-to-cart__image img');

    if (titleElement && title) titleElement.textContent = title;
    if (priceElement && price) priceElement.textContent = price;
    if (imageElement && image) imageElement.src = image;
  }

  bindQuantityButtons() {
    const quantityInput = this.stickyForm?.querySelector('input[name="quantity"]');
    const increaseBtn = this.stickyButton?.querySelector('.quantity__btn--increase');
    const decreaseBtn = this.stickyButton?.querySelector('.quantity__btn--decrease');

    if (!quantityInput || !increaseBtn || !decreaseBtn) return;

    increaseBtn.addEventListener('click', () => {
      const currentValue = parseInt(quantityInput.value) || 1;
      quantityInput.value = currentValue + 1;
      this.syncQuantityToMain();
    });

    decreaseBtn.addEventListener('click', () => {
      const currentValue = parseInt(quantityInput.value) || 1;
      if (currentValue > 1) {
        quantityInput.value = currentValue - 1;
        this.syncQuantityToMain();
      }
    });

    quantityInput.addEventListener('input', () => {
      this.syncQuantityToMain();
    });
  }

  syncQuantityToMain() {
    const stickyQuantityInput = this.stickyForm?.querySelector('input[name="quantity"]');
    const mainQuantityInput = this.mainForm?.querySelector('input[name="quantity"]');
    
    if (stickyQuantityInput && mainQuantityInput) {
      mainQuantityInput.value = stickyQuantityInput.value;
    }
  }

  // Public method to destroy the instance
  destroy() {
    window.removeEventListener('scroll', this.checkVisibility);
    if (this.throttleTimer) {
      clearTimeout(this.throttleTimer);
    }
    if (this.priceObserver) {
      this.priceObserver.disconnect();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Only initialize on product pages
  if (document.body.classList.contains('template-product')) {
    new StickyAddToCart();
  }
});

// Also initialize if script loads after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.body.classList.contains('template-product')) {
      new StickyAddToCart();
    }
  });
} else if (document.body.classList.contains('template-product')) {
  new StickyAddToCart();
}
